import { ApiDimension, ApiOperator } from "@incendium/api";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Autocomplete, IconButton, Stack, TextField } from "@mui/material";
import { IChart } from "Interfaces";
import { NOT_SET, useFilterableDimensions } from "features/analytics";
import produce from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUpdateEffect } from "react-use";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { uniqueArray } from "Helpers/arrays";

interface IAnalyticsFilterDropdownProps {
  dimension: ApiDimension;
  setChart?: React.Dispatch<React.SetStateAction<IChart>>;
  defaultFirstFilter?: { [key in ApiDimension]?: string };
  isReady?: () => void;
  onChange?: (e: string) => void;
  defaultIndex?: number | ((a: string[]) => number);
  maxWidth?: number;
  disableBlank?: boolean;
  blankLabel?: string;
  label?: string;
}

function AnalyticsFilterDropdown({
  dimension,
  setChart,
  defaultFirstFilter,
  isReady,
  onChange,
  defaultIndex,
  maxWidth,
  disableBlank,
  blankLabel = "All Users",
  label,
}: IAnalyticsFilterDropdownProps) {
  const dimensionToName = useDimensionName();
  const { getDimensionValues, filterableDimensions, loading } =
    useFilterableDimensions();
  const [selectedValue, setSelectedValue] = useState("");
  const [index, setIndex] = useState(() => {
    if (typeof defaultIndex !== "number") {
      return -1;
    }
    return disableBlank ? defaultIndex : defaultIndex + 1;
  });
  const [defaultSet, setDefaultSet] = useState(false);
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Compute availableOptions: if dimension is FETCHED, use names from filterableDimensions; else use stateOptions
  const availableOptions = useMemo(() => {
    if (filterableDimensions[dimension]?.status === "FETCHED") {
      return (filterableDimensions[dimension].names || []).filter(f => f !== NOT_SET);
    }
    return stateOptions;
  }, [filterableDimensions, dimension, stateOptions]);

  // Compute options, including 'All Users' if disableBlank is false
  const optionsWithBlank = useMemo(() => disableBlank
    ? availableOptions
    : [blankLabel, ...availableOptions.filter((opt) => opt !== blankLabel)],
    [disableBlank, availableOptions, blankLabel]
  );

  // populate options based on the dimension prop
  const fetchOptions = useCallback(
    async (dimension) => {
      setLoadingOptions(true);
      try {
        const dimensionValues = await getDimensionValues(dimension);
        const filteredOptions = (dimensionValues || []).filter(
          (f) => f !== NOT_SET
        );

        setStateOptions(uniqueArray(filteredOptions));
      } catch (error) {
        console.error("Error fetching dimension values:", error);
        setStateOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    },
    [getDimensionValues]
  );

  useEffect(() => {
    if (!dimension || !filterableDimensions[dimension]) {
      return;
    }
    if (filterableDimensions[dimension].status === "FETCHED") {
      // No need to set options, handled by useMemo
      return;
    }
    fetchOptions(dimension);
  }, [dimension, fetchOptions, filterableDimensions]);

  const toOptionsIndex = useCallback(
    (availableIndex: number) =>
      disableBlank ? availableIndex : availableIndex + 1,
    [disableBlank],
  );

  useUpdateEffect(() => {
    if (typeof defaultIndex !== "function") {
      return;
    }
    const availableIndex = defaultIndex(availableOptions);
    const nextIndex = toOptionsIndex(availableIndex);
    if (nextIndex !== index) {
      setIndex(nextIndex);
    }
  }, [defaultIndex]);

  useEffect(() => {
    if (loading || loadingOptions || index >= 0 || defaultSet) {
      return;
    }
    if (defaultFirstFilter && defaultFirstFilter[dimension]) {
      const availableIndex = availableOptions.findIndex(
        (a) => a === defaultFirstFilter[dimension],
      );
      if (availableIndex >= 0) {
        setIndex(toOptionsIndex(availableIndex));
      }
    } else if (typeof defaultIndex === "function") {
      setIndex(toOptionsIndex(defaultIndex(availableOptions)));
    }
    setDefaultSet(true);
    isReady && isReady();
  }, [
    loading,
    loadingOptions,
    availableOptions,
    defaultFirstFilter,
    defaultIndex,
    dimension,
    index,
    isReady,
    defaultSet,
    toOptionsIndex,
  ]);

  useUpdateEffect(() => {
    if (!onChange && !setChart) {
      return;
    }
    if (onChange) {
      onChange(selectedValue);
      return;
    }
    if (setChart) {
      setChart((chart) =>
        produce(chart, (draft) => {
          const idx = (draft.attributes || []).findIndex(
            (f) => f.key === dimension
          );
          if (idx >= 0) {
            if (!selectedValue) {
              draft.attributes.splice(idx, 1);
              return;
            }

            draft.attributes[idx] = {
              ...draft.attributes[idx],
              value: selectedValue,
            };
          } else {
            draft.attributes.push({
              key: dimension,
              operator: ApiOperator.EQUAL,
              value: selectedValue,
            });
          }
        })
      );
    }
  }, [selectedValue]);

  const onCycle = useCallback(
    (direction: "left" | "right") => {
      setIndex((prev) => {
        const maxIndex = optionsWithBlank.length - 1;
        const newIndex = direction === "right" ? prev + 1 : prev - 1;
        if (newIndex > maxIndex) {
          return 0;
        }
        if (newIndex < 0) {
          return maxIndex;
        }
        return newIndex;
      });
    },
    [optionsWithBlank],
  );

  useEffect(() => {
    if (index < 0) {
      setSelectedValue("");
      return;
    }
    if (index >= optionsWithBlank.length) {
      return;
    }
    const option = optionsWithBlank[index];
    setSelectedValue(option === blankLabel ? "" : option);
  }, [index, optionsWithBlank, blankLabel]);

  // Map value for Autocomplete: if selectedValue is '', show 'All Users'
  const displayValue =
    !disableBlank && (!selectedValue || selectedValue === blankLabel)
      ? blankLabel
      : selectedValue;

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      sx={{
        maxWidth: maxWidth || "100%",
        width: maxWidth || 264,
        minWidth: 160,
      }}
    >
      <IconButton
        size="small"
        onClick={() => onCycle("left")}
        color="secondary"
        disabled={loading}
      >
        <ChevronLeft />
      </IconButton>

      <Autocomplete
        id="att-value"
        loading={loading || loadingOptions}
        disabled={loading}
        onChange={(e, v) => {
          if (v == null) {
            setIndex(-1);
            return;
          }
          setIndex(optionsWithBlank.findIndex((a) => a === v));
        }}
        value={displayValue}
        filterSelectedOptions
        disableClearable={disableBlank}
        sx={{ flex: 1 }}
        fullWidth
        options={optionsWithBlank}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label || `Select ${dimensionToName(dimension)}`}
            variant="outlined"
            size="small"
          />
        )}
      />
      <IconButton
        size="small"
        onClick={() => onCycle("right")}
        color="secondary"
        disabled={loading}
      >
        <ChevronRight />
      </IconButton>
    </Stack>
  );
}

export default AnalyticsFilterDropdown;
